// Heart monetization stubs — swap bodies for real rewarded ads / IAP later.
// Preview mode grants rewards locally so the empty-heart funnel can be tested.

import { purchaseHeartRefillProduct } from "./iapStore";

const LS_REWARDED_AD_DAY_KEY = "mmu_heart_rewarded_ad_day_v1";
const LS_REWARDED_AD_COUNT_KEY = "mmu_heart_rewarded_ad_count_v1";

/** Daily cap for rewarded +1 heart ads (stub). */
export const HEART_REWARDED_AD_DAILY_CAP = 5;

/** Simulated latency when no visual ad modal is shown (tests / fallback). */
export const HEART_REWARD_STUB_DELAY_MS = 650;

function todayKey(now = Date.now()) {
    return new Date(now).toISOString().slice(0, 10);
}

function readRewardedAdUsage(now = Date.now()) {
    try {
        const day = localStorage.getItem(LS_REWARDED_AD_DAY_KEY);
        const count = Number(localStorage.getItem(LS_REWARDED_AD_COUNT_KEY));
        if (day !== todayKey(now) || !Number.isFinite(count)) {
            return { day: todayKey(now), count: 0 };
        }
        return { day, count: Math.max(0, Math.floor(count)) };
    } catch {
        return { day: todayKey(now), count: 0 };
    }
}

function writeRewardedAdUsage(count, now = Date.now()) {
    try {
        localStorage.setItem(LS_REWARDED_AD_DAY_KEY, todayKey(now));
        localStorage.setItem(
            LS_REWARDED_AD_COUNT_KEY,
            String(Math.max(0, Math.floor(count)))
        );
    } catch {
        // ignore localStorage failures
    }
}

export function getRewardedHeartAdStatus(now = Date.now(), options = {}) {
    if (options.adsRemoved) {
        return {
            available: false,
            remainingToday: 0,
            dailyCap: HEART_REWARDED_AD_DAILY_CAP,
            usedToday: 0,
            isPreview: true,
            adsRemoved: true,
        };
    }

    const usage = readRewardedAdUsage(now);
    const remaining = Math.max(0, HEART_REWARDED_AD_DAILY_CAP - usage.count);

    return {
        available: remaining > 0,
        remainingToday: remaining,
        dailyCap: HEART_REWARDED_AD_DAILY_CAP,
        usedToday: usage.count,
        /** True until a real ad SDK is wired. */
        isPreview: true,
        adsRemoved: false,
    };
}

function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Stub rewarded ad: enforces daily cap, then grants +1 heart worth of credit.
 * Replace with AdMob / AppLovin / etc. when ready.
 */
export async function watchRewardedAdForHeart(now = Date.now(), options = {}) {
    const status = getRewardedHeartAdStatus(now, options);

    if (status.adsRemoved) {
        const error = new Error("Ads are removed on this account.");
        error.code = "ads-removed";
        throw error;
    }

    if (!status.available) {
        const error = new Error("Daily rewarded-ad limit reached.");
        error.code = "ad-cap";
        throw error;
    }

    if (!options.skipDelay) {
        await delay(HEART_REWARD_STUB_DELAY_MS);
    }

    writeRewardedAdUsage(status.usedToday + 1, now);

    return {
        granted: true,
        hearts: 1,
        isPreview: true,
        remainingToday: Math.max(0, status.remainingToday - 1),
    };
}

/**
 * Heart IAP entry point used by empty-heart / recap funnels.
 * Delegates to the shared store service (preview today, native later).
 * @param {{ pack?: "full" | "single", productId?: string }} [options]
 */
export async function purchaseHeartRefill(options = {}) {
    const result = await purchaseHeartRefillProduct(options);

    return {
        granted: Boolean(result?.granted),
        pack: result?.grant?.refillToMax ? "full" : "single",
        productId: result?.productId,
        hearts: Number(result?.grant?.hearts) || 0,
        refillToMax: Boolean(result?.grant?.refillToMax),
        isPreview: Boolean(result?.isPreview),
    };
}
